import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CardWrapper,
  BentoImage,
  BentoOverlay,
  LocationText,
  Overlay,
  FullScreenImage,
  FullScreenTextContainer,
  FullScreenTitle,
  FullScreenMeta,
  CloseButton,
  MinimizeLine
} from './PhotoCard.styles';

// Typewriter Helper (Duplicated for standalone use)
const TypewriterText = ({ text, delay = 0, as = "div", className }) => {
  const sentenceVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: { delayChildren: delay, staggerChildren: 0.04 },
    },
  };

  const letterVariants = {
    hidden: { opacity: 0, y: 5 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.01 } },
  };

  return (
    <motion.span
      as={as}
      className={className}
      variants={sentenceVariants}
      initial="hidden"
      animate="visible"
      aria-label={text}
    >
      {text.split("").map((char, index) => (
        <motion.span key={`${char}-${index}`} variants={letterVariants} aria-hidden="true">
          {char}
        </motion.span>
      ))}
    </motion.span>
  );
};

// ... (keep your existing imports and TypewriterText component) ...

const PhotoCard = ({ photo, index, variant }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // --- NEW ESCAPE KEY LOGIC ---
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    // Only attach the listener if the portal is currently open
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    // Cleanup function: remove the listener when the component unmounts 
    // or when isOpen changes to false
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]); 
  // ----------------------------
  // Prevent background scrolling and viewport shifting while modal is open
  // 1. Update the Scroll Lock (Targeting document.documentElement)
  useEffect(() => {
    if (isOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      
      // Lock both HTML and Body to prevent mobile viewport jumps
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '0px';
    }

    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '0px';
    };
  }, [isOpen]);

  if (!photo) return null;

  const { title, image, location } = photo;
  const layoutId = `photo-${title.replace(/\s+/g, '-').toLowerCase()}-${index}`;

  return (
    <>
      {/* 1. Bento Grid View */}
      <CardWrapper
        $variant={variant}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
        whileHover="hover" 
      >
        <BentoImage 
          src={image} 
          alt={`Photo: ${title}`} 
          layoutId={layoutId} 
          //style={{ visibility: isOpen ? 'hidden' : 'visible' }}
          variants={{
            hover: { scale: 1.05, transition: { duration: 0.4, ease: "easeOut" } }
          }}
        />
        
        <BentoOverlay>
          <LocationText>{location}</LocationText>
        </BentoOverlay>
      </CardWrapper>

      {/* 2. Full Screen Portal View */}
      {ReactDOM.createPortal(
        <AnimatePresence>
          {isOpen && (
            <Overlay
              // 1. Start with pointer events disabled
              initial={{ opacity: 0, pointerEvents: "none" }}
              
              // 2. Enable pointer events ONLY once fully animated in
              animate={{ opacity: 1, pointerEvents: "auto" }}
              
              // 3. Disable again on exit to prevent double-clicks during close
              exit={{ opacity: 0, pointerEvents: "none" }}
              
              onClick={() => setIsOpen(false)} 
            >
              <FullScreenImage 
                src={image} 
                alt={title}
                layoutId={layoutId}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                onClick={(e) => e.stopPropagation()}
              />

              <FullScreenTextContainer>
                <FullScreenTitle as="h2">
                  <TypewriterText text={title} delay={0.5} />
                </FullScreenTitle>
                <FullScreenMeta>
                  <TypewriterText text={location} delay={0.5 + (title.length * 0.04)} />
                </FullScreenMeta>
              </FullScreenTextContainer>

              <CloseButton
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                }}
                whileHover="hover"
                whileTap="tap"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <MinimizeLine variants={{ hover: { y: -5 }, tap: { scale: 0.9 } }} />
              </CloseButton>
            </Overlay>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default PhotoCard;